import { HeadersFunction, LoaderFunction, MetaFunction } from "react-router";
import { useLoaderData, useNavigate, useSearchParams } from "react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsPauseFill } from "react-icons/bs";
import { CiMusicNote1 } from "react-icons/ci";
import RecipeBhajanView from "~/components/recipe/RecipeBhajanView";
import RecipeListView from "~/components/recipe/RecipeListView";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import TabComponent from "~/components/shared-components/TabComponent";
import { syncData } from "~/data/thalPlayerSyncData";
import { RecipeLoaderData } from "~/interface/recipe/recipe";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { removeAudioPlayerKeyFromSession } from "~/lib/helperFuncation/sessionStorageService";
import { addToCurrentAudio, changeIsAudioPlayState, changeIsPlayingState, removeQueue, setIsAnnakutAudioPlaying } from "~/lib/store/slice/audioSlice";
import { setRecipeCategories } from "~/lib/store/slice/recipeSlice";
import { onPauseVideo } from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { MstPersonType } from "~/type";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, ICON_ORANGE_COLOR, LANG } from "~/utils/constant";
import { computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";

  const meta = getPageMeta("recipe", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader: LoaderFunction = async ({ params, request }) => {
  const lang = params.lang || "en";
  const apiService = new ApiService(lang || LANG.english);
  const searchParams = new URL(request.url).searchParams;
  const category = searchParams.get("category");
  const selectedView = searchParams.get("view");

  //get recipe categories from MAster API
  const masterdataResponse = await apiService.getMasterData();
  const recipeCategories = masterdataResponse?.recipe_category || [];

  //find selected category id from recipeCategories
  const selectedCategory = recipeCategories.find((categoryItem: MstPersonType) => categoryItem.text.toLowerCase() === category?.toLowerCase());
  const subCategory = selectedCategory ? selectedCategory.id : null;

  //get recipe data
  const recipeResponse = await apiService.getRecipeData({
    pageIndex: 1,
    pageSize: 36,
    subCategory: subCategory,
  });
  const recipeData = recipeResponse?.rows;
  const responseCount = recipeResponse?.count || 0;

  const DATA = {
    title: lang === LANG.english ? "Annkut aarogo" : "અન્નકુટ આરોગ",
    recipeData,
    recipeCategories,
    selectedCategory,
    responseCount,
    selectedView,
  };

  const etag = computeEtagFromData(DATA);
  const ifNoneMatch = request.headers.get("If-None-Match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.THAL.HEADERS, ETag: etag },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
};

type recipe = "bhajan" | "list";
interface TabDef {
  key: recipe;
  label: string;
}

function Recipe() {
  const { t } = useTranslation();
  const bhajanViewRef = useRef<HTMLDivElement>(null);
  const { recipeData, recipeCategories, selectedCategory, responseCount, selectedView } = useLoaderData<RecipeLoaderData>();
  const { is_annkut_audio_playing: isPlaying } = useAppSelector((state) => state.nityNiyam);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabs: TabDef[] = [
    { key: "bhajan", label: t("recipe.bhajanView") },
    { key: "list", label: t("recipe.listView") },
  ];

  const [selectedTab, setSelectedTab] = useState<recipe>("bhajan");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const handleChangeTab = (value: recipe) => {
    setSearchParams({ view: value }, { replace: true });
    setSelectedTab(value);
  };

  const handleOnAudioPlay = () => {
    if (!isPlaying) {
      dispatch(setIsAnnakutAudioPlaying(true));
      dispatch(onPauseVideo());
      dispatch(changeIsAudioPlayState(true));
      dispatch(changeIsPlayingState(true));
      dispatch(removeQueue());
      dispatch(
        addToCurrentAudio({
          id: 1,
          img: "https://d1maxl27td41jk.cloudfront.net/files/Audio/Audio-Kirtan/Custom-Tracks/Annakut-Aarogo-medium.jpg",
          totalDuration: "1:15:35",
          artist: "Gyanjivandasji Swami - Kundaldham",
          name: "Annakut Aarogo",
          audio: "https://media.swaminarayanbhagwan.com/files/Kirtans/AnnakutAarogo/Annakut%20Aarogo%20Thal-128.mp3",
          from: "annakut_aarogo",
        }),
      );

      setTimeout(() => {
        bhajanViewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      dispatch(changeIsAudioPlayState(false));
      dispatch(changeIsPlayingState(false));
      removeAudioPlayerKeyFromSession();
      dispatch(setIsAnnakutAudioPlaying(false));
    }
  };

  useEffect(() => {
    dispatch(setRecipeCategories(recipeCategories));
  }, [recipeCategories]);

  useEffect(() => {
    if (!searchParams.get("view")) {
      navigate(`?view=${"bhajan"}`, { replace: true });
      setSelectedTab("bhajan");
    } else {
      const viewParam = searchParams.get("view");
      setSelectedTab(viewParam === "list" ? "list" : "bhajan");
    }
  }, []);

  useEffect(() => {
    let Thalplayer: HTMLMediaElement | null = null;

    const handlePlayer = () => {
      const currentTime = Thalplayer?.currentTime ?? 0;
      const currentLine = [...syncData].reverse().find((line) => currentTime >= line.start);

      if (!currentLine) return;

      const pTags = document.querySelectorAll(".thallyrics p");
      if (currentLine.text >= pTags.length) return;

      pTags.forEach((el) => el.classList.remove("ishighlighted"));

      const highlightEl = pTags[currentLine.text];
      if (highlightEl) {
        highlightEl.classList.add("ishighlighted");

        const rect = highlightEl.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

        if (!isVisible) {
          highlightEl.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    };

    const bindEvent = () => {
      Thalplayer = document.querySelector(".Thalplayer") as HTMLMediaElement | null;
      if (isPlaying && Thalplayer) {
        Thalplayer.addEventListener("timeupdate", handlePlayer);
      }
    };

    // Wait until DOM is fully ready
    const timeout = setTimeout(bindEvent, 0);

    return () => {
      clearTimeout(timeout);
      Thalplayer?.removeEventListener("timeupdate", handlePlayer);
    };
  }, [isPlaying]);

  return (
    <div className="w-full relative mb-5">
      <div className="w-full relative flex justify-center items-center max-lg:px-3">
        <TabComponent tabs={tabs} selectedTab={selectedTab} onTabChange={(value) => handleChangeTab(value as recipe)} />
        <div
          className="w-auto lyrics rounded-full bg-secondaryBackground hover:bg-hoverColor cursor-pointer border border-borderPrimary p-1.5 flex justify-center items-center gap-2 text-textHeadingColor"
          onClick={handleOnAudioPlay}
        >
          {isPlaying ? <BsPauseFill size={20} color={ICON_ORANGE_COLOR} /> : <CiMusicNote1 size={20} color={ICON_ORANGE_COLOR} />}
        </div>
      </div>

      {selectedTab === "bhajan" ? (
        <div ref={bhajanViewRef}>
          <RecipeBhajanView />
        </div>
      ) : (
        <RecipeListView recipeData={recipeData} selectedCategory={selectedCategory} responseCount={responseCount} />
      )}
    </div>
  );
}

export default Recipe;
